"use client";
import Image from "next/image";
import { ButtonLink } from "../button-link";
import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import { Heading2 } from "../typography";
import { ReactNode } from "react";

interface CTAButton {
  text: string;
  href: string;
  backgroundColorClass: string;
}

interface CTASectionProps {
  id?: string;
  title: string | ReactNode;
  description?: string | ReactNode;
  secondaryDescription?: string | ReactNode;
  buttons: CTAButton[];
  backgroundGradient?: string;
  className?: string;
}

export const CTASection = ({
  id = "contact-us",
  title,
  description,
  secondaryDescription,
  buttons,
  backgroundGradient = "from-haizea-dark via-haizea-blue/40 to-slate-900",
  className = "",
}: CTASectionProps) => {
  useSectionNavTheme({
    trigger: ".cta-section",
    isDark: true,
  });

  return (
    <section
      id={id}
      className={`relative cta-section overflow-hidden bg-white dark:bg-haizea-dark dark:bg-gradient-to-tr ${backgroundGradient} ${className}`}
    >
      <div className="container">
        <div className="py-20 md:py-32 lg:py-44 flex items-center">
          <div className="flex flex-col max-w-5xl">
            <Heading2
              data-speed="0.9"
              className="text-slate-700 dark:text-slate-200"
            >
              {title}
            </Heading2>

            {description && (
              <p
                className="text-xl md:text-2xl text-slate-600 dark:text-slate-300 mb-8 leading-relaxed"
                data-speed="0.98"
              >
                {description}
              </p>
            )}

            {secondaryDescription && (
              <p
                className="text-lg text-slate-600 dark:text-slate-200 mb-12"
                data-speed="0.98"
              >
                {secondaryDescription}
              </p>
            )}

            <div
              id="cta-buttons"
              className="flex items-center gap-6 lg:gap-8 flex-wrap"
            >
              {buttons.map((button, index) => (
                <ButtonLink
                  key={index}
                  backgroundColorClass={button.backgroundColorClass}
                  href={button.href}
                  textClassName="whitespace-break-spaces"
                >
                  {button.text}
                </ButtonLink>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Image
        src="/images/satellitewave-halftone-bluegreen.webp"
        alt="CTA Background"
        width={653}
        height={264}
        className="hidden lg:block h-30 md:h-44 xl:h-64 w-auto object-contain absolute right-0 bottom-[5vw]"
        data-speed="0.75"
        draggable={false}
      />
    </section>
  );
};
